// app/components/AdvancedChart/hooks/useUpdateChartData.ts
import { useMemo, useCallback } from 'react'
import { UTCTimestamp, LineStyle, ISeriesApi, Time } from 'lightweight-charts'
import { calculateRSI } from '../utils/indicators'
import { formatPrice } from '../utils/priceFormatter'
import { toUTCTimestamp } from '../utils/time'

/**
 * Hook to update chart series data (candlesticks, volume, RSI if drawn on main)
 * and plot open orders. Now uses controlled updates instead of automatic useEffect.
 *
 * @param chartData - Array containing the candle data.
 * @param openOrders - Array of open order objects.
 * @param currentSymbol - The active symbol (used to match orders).
 * @param mainSeriesRef - Ref to the main candlestick series.
 * @param volumeSeriesRef - Ref to the volume histogram series.
 * @param setLastPrice - Setter to update last price state.
 * @param lastPriceRef - Mutable ref holding the last known price.
 * @param setOpenOrderLines - Setter to update the list of order lines plotted on the chart.
 */
interface UseUpdateChartDataProps {
  chartData: any[]
  openOrders: any[]
  currentSymbol: string
  mainSeriesRef: React.MutableRefObject<ISeriesApi<'Candlestick'> | null>
  volumeSeriesRef: React.MutableRefObject<ISeriesApi<'Histogram'> | null>
  setLastPrice: (price: number) => void
  lastPriceRef: React.MutableRefObject<number | null>
  // Allow functional updates for order lines.
  setOpenOrderLines: React.Dispatch<React.SetStateAction<any[]>>
}

export function useUpdateChartData({
  chartData,
  openOrders,
  currentSymbol,
  mainSeriesRef,
  volumeSeriesRef,
  setLastPrice,
  lastPriceRef,
  setOpenOrderLines
}: UseUpdateChartDataProps) {
  // Memoize candle data processing
  const candleData = useMemo(() => {
    if (!chartData.length) return []
    
    return chartData.map(d => ({
      time: toUTCTimestamp(d.time as Time)!,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close
    }))
  }, [chartData])

  // Memoize volume data processing
  const volumeData = useMemo(() => {
    if (!chartData.length) return []
    
    return chartData.map(d => ({
      time: toUTCTimestamp(d.time as Time)!,
      value: d.volume,
      color: d.close > d.open ? '#2DBD8580' : '#F6465D80'
    }))
  }, [chartData])

  // Memoize last price calculation
  const lastPrice = useMemo(() => {
    if (!chartData.length) return null
    const lastCandle = chartData[chartData.length - 1]
    return lastCandle.close
  }, [chartData])

  // Memoize matched orders filtering
  const matchedOrders = useMemo(() => {
    return openOrders.filter(
      o => o.symbol.toLowerCase() === currentSymbol.toLowerCase()
    )
  }, [openOrders, currentSymbol])

  // Memoize order line creation function
  const createOrderLines = useCallback(() => {
    if (!mainSeriesRef.current) return []

    const newLines: any[] = []

    matchedOrders.forEach(order => {
      const sideColor = order.side === 'BUY' ? '#2DBD85' : '#F6465D'
      
      // Check if this is a GTT order from Zerodha
      const isGTTOrder = order.type === 'GTT'
      
      // Create a special label for GTT orders
      const sideLabel = isGTTOrder 
        ? `GTT-[${order.side}]` 
        : `[${order.side}]`

      const limitLine = mainSeriesRef.current?.createPriceLine({
        price: order.limitPrice,
        color: sideColor,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        axisLabelVisible: true,
        title: sideLabel
      })
      if (limitLine) newLines.push(limitLine)

      if (order.stopLoss !== undefined && order.stopLoss !== null && order.stopLoss !== 0) {
        const isBuy = order.side === 'BUY'
        const diff = isBuy
          ? (order.stopLoss - order.limitPrice) * order.size
          : (order.limitPrice - order.stopLoss) * order.size
        const pct = (diff / (order.limitPrice * order.size)) * 100
        const slLine = mainSeriesRef.current?.createPriceLine({
          price: order.stopLoss,
          color: '#ff3333',
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: `[SL: ${diff >= 0 ? '+' : ''}₹${formatPrice(diff)} (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)]`
        })
        if (slLine) newLines.push(slLine)
      }

      if (order.takeProfit !== undefined && order.takeProfit !== null && order.takeProfit !== 0) {
        const isBuy = order.side === 'BUY'
        const diff = isBuy
          ? (order.takeProfit - order.limitPrice) * order.size
          : (order.limitPrice - order.takeProfit) * order.size
        const pct = (diff / (order.limitPrice * order.size)) * 100
        const tpLine = mainSeriesRef.current?.createPriceLine({
          price: order.takeProfit,
          color: '#33ff33',
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          axisLabelVisible: true,
          title: `[TP: ${diff >= 0 ? '+' : ''}₹${formatPrice(diff)} (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)]`
        })
        if (tpLine) newLines.push(tpLine)
      }
    })

    return newLines
  }, [matchedOrders, mainSeriesRef])

  // Controlled update function instead of automatic useEffect
  const performUpdates = useCallback(() => {
    if (!chartData.length) return false
    if (!mainSeriesRef.current || !volumeSeriesRef.current) return false

    // Update candlestick series data
    mainSeriesRef.current.setData(candleData)

    // Update volume histogram data
    volumeSeriesRef.current.setData(volumeData)

    // Update last price
    if (lastPrice !== null) {
      setLastPrice(lastPrice)
      lastPriceRef.current = lastPrice
    }

    // Plot open orders - remove existing lines first
    setOpenOrderLines((prevLines: any[]) => {
      prevLines.forEach((line: any) => {
        mainSeriesRef.current?.removePriceLine(line)
      })
      return createOrderLines()
    })

    return true
  }, [
    chartData.length,
    mainSeriesRef,
    volumeSeriesRef,
    candleData,
    volumeData,
    lastPrice,
    setLastPrice,
    lastPriceRef,
    setOpenOrderLines,
    createOrderLines
  ])

  // Use useMemo to call performUpdates when dependencies change
  // This replaces the automatic useEffect behavior with controlled updates
  const updateResult = useMemo(() => {
    return performUpdates()
  }, [performUpdates])

  // Return the update function and processed data for external control if needed
  return {
    performUpdates,
    candleData,
    volumeData,
    lastPrice,
    matchedOrders,
    updateResult
  }
}
